import { MediaDisplay } from "../../../resources/modelTypes";

export type AssetSpec = {
  algorithm: "crop";
  height: number;
  key: string;
  slotIndex?: number;
  width: number;
};

/** Legacy per-theme crop dimensions; modular slots are cropped to these same boxes. */
export const assetSpecs = {
  l1: {
    themeImage: {
      algorithm: "crop" as const,
      height: 1300,
      key: "theme_image",
      width: 2400,
    },
  },
  l2: {
    brideImage: {
      algorithm: "crop" as const,
      height: 1000,
      key: "bride_image",
      width: 1000,
    },
    groomImage: {
      algorithm: "crop" as const,
      height: 1000,
      key: "groom_image",
      width: 1000,
    },
  },
  l3: {
    themeImage: {
      algorithm: "crop" as const,
      height: 2134,
      key: "theme_image",
      width: 3200,
    },
  },
};

/** Crop specs for modular media slots (legacy l1/l2/l3 dimensions). */
export const modularMediaSpecs = {
  fullBleed: {
    ...assetSpecs.l3.themeImage,
    key: "header_slot_0",
    slotIndex: 0,
  },
  landscape: {
    ...assetSpecs.l1.themeImage,
    key: "header_slot_0",
    slotIndex: 0,
  },
  // Portrait slots for the overlapping collage (3:4).
  overlapping: [
    {
      algorithm: "crop" as const,
      height: 1200,
      key: "header_slot_0",
      slotIndex: 0,
      width: 900,
    },
    {
      algorithm: "crop" as const,
      height: 1200,
      key: "header_slot_1",
      slotIndex: 1,
      width: 900,
    },
  ],
  // Natural-aspect pair (same height, variable widths) — landscape crop box.
  sideBySide: [
    {
      ...assetSpecs.l1.themeImage,
      key: "header_slot_0",
      slotIndex: 0,
    },
    {
      ...assetSpecs.l1.themeImage,
      key: "header_slot_1",
      slotIndex: 1,
    },
  ],
  square: [
    {
      ...assetSpecs.l2.brideImage,
      key: "header_slot_0",
      slotIndex: 0,
    },
    {
      ...assetSpecs.l2.groomImage,
      key: "header_slot_1",
      slotIndex: 1,
    },
  ],
} as const;

/**
 * Aspect ratio header photos are cropped to for the Landscape/SideBySide/
 * Slideshow/Marquee treatments, so the live display components can size
 * themselves to match instead of an unrelated fixed pixel height.
 */
export const LANDSCAPE_MEDIA_ASPECT_RATIO =
  assetSpecs.l1.themeImage.width / assetSpecs.l1.themeImage.height;

/** Crop box for a modular header slot at the given position. */
export const modularSlotAssetSpec = (
  slotIndex: number,
  cropSpec: Pick<AssetSpec, "algorithm" | "height" | "width">,
): AssetSpec => ({
  ...cropSpec,
  key: `header_slot_${slotIndex}`,
  slotIndex,
});

/**
 * The crop spec (aspect ratio + minimum export size) a header photo at
 * `position` should be cropped to for the site's current `mediaDisplay`.
 * Marquee/Slideshow have no per-slot spec of their own — they reuse the
 * landscape crop box, matching the legacy pencil-overlay behavior. `None`
 * has no visible slot, so there's nothing to crop against.
 */
export const getHeaderSlotCropSpec = (
  mediaDisplay: MediaDisplay,
  position: number,
): AssetSpec | null => {
  switch (mediaDisplay) {
    case MediaDisplay.Square:
      return modularMediaSpecs.square[position] ?? null;
    case MediaDisplay.SideBySide:
      return modularMediaSpecs.sideBySide[position] ?? null;
    case MediaDisplay.Overlapping:
      return modularMediaSpecs.overlapping[position] ?? null;
    case MediaDisplay.FullBleed:
      return modularSlotAssetSpec(position, assetSpecs.l3.themeImage);
    case MediaDisplay.Landscape:
    case MediaDisplay.Marquee:
    case MediaDisplay.Slideshow:
      return modularSlotAssetSpec(position, assetSpecs.l1.themeImage);
    case MediaDisplay.None:
    default:
      return null;
  }
};
